/**
 * Canonical portfolio route contract.
 *
 * Resource routes (per the PR #284 follow-up):
 *   /carteira                                  -> redirect to default portfolio resumo
 *   /carteira/nova                             -> create flow (replaces ?nova=1)
 *   /carteira/{id}/resumo                      -> default tab
 *   /carteira/{id}/ativos
 *   /carteira/{id}/dividendos                  -> dividend overview
 *   /carteira/{id}/dividendos/lancamentos      -> dividend landings (sub-aba)
 *   /carteira/{id}/patrimonio
 *   /carteira/{id}/patrimonio/diversificacao   -> patrimony diversification
 *   /carteira/{id}/movimentacoes
 *   /carteira/{id}/relatorios
 *   /carteira/{id}/emails
 *   /carteira/{id}/importacoes
 *   /carteira/{id}/processamento               -> BACKLOG-018 processing diagnostics
 *   /carteira/{id}/configuracao                -> configuration overview
 *   /carteira/{id}/configuracao/emails
 *   /carteira/{id}/configuracao/importacoes
 *   /carteira/{id}/configuracao/processamento
 *
 * Query params are reserved for *filters* (date ranges, indicators, presets,
 * amortisation toggles, dividend sub-view flags kept internal to the
 * dividendos landing page, etc.) and import session tracking.
 */

export const CARTEIRA_PRIMARY_TABS = [
  'resumo',
  'ativos',
  'dividendos',
  'patrimonio',
  'movimentacoes',
  'relatorios',
  'emails',
  'importacoes',
  'processamento',
  'configuracao',
] as const;

export type CarteiraPrimaryTab = (typeof CARTEIRA_PRIMARY_TABS)[number];

export const CARTEIRA_DIVIDEND_SUBVIEWS = ['visao-geral', 'lancamentos'] as const;

export type CarteiraDividendSubview = (typeof CARTEIRA_DIVIDEND_SUBVIEWS)[number];

export const CARTEIRA_PATRIMONY_SUBVIEWS = ['visao-geral', 'diversificacao'] as const;

export type CarteiraPatrimonySubview = (typeof CARTEIRA_PATRIMONY_SUBVIEWS)[number];

export const CARTEIRA_CONFIG_SUBVIEWS = [
  'geral',
  'compartilhamento',
  'emails',
  'importacoes',
  'processamento',
] as const;

export type CarteiraConfigSubview = (typeof CARTEIRA_CONFIG_SUBVIEWS)[number];

export const CARTEIRA_NEW_PATH = '/carteira/nova' as const;
export const CARTEIRA_INDEX_PATH = '/carteira' as const;

const CARTEIRA_SEGMENT_TO_LABEL: Record<CarteiraPrimaryTab, string> = {
  resumo: 'Resumo',
  ativos: 'Posições',
  dividendos: 'Dividendos',
  patrimonio: 'Patrimônio',
  movimentacoes: 'Movimentações',
  relatorios: 'Relatórios',
  emails: 'E-mails',
  importacoes: 'Importações',
  processamento: 'Processamento',
  configuracao: 'Configuração',
};

const isCarteiraPrimaryTab = (value: string): value is CarteiraPrimaryTab =>
  (CARTEIRA_PRIMARY_TABS as readonly string[]).includes(value);

const isCarteiraDividendSubview = (value: string): value is CarteiraDividendSubview =>
  (CARTEIRA_DIVIDEND_SUBVIEWS as readonly string[]).includes(value);

const isCarteiraPatrimonySubview = (value: string): value is CarteiraPatrimonySubview =>
  (CARTEIRA_PATRIMONY_SUBVIEWS as readonly string[]).includes(value);

const isCarteiraConfigSubview = (value: string): value is CarteiraConfigSubview =>
  (CARTEIRA_CONFIG_SUBVIEWS as readonly string[]).includes(value);

export const labelForCarteiraTab = (tab: CarteiraPrimaryTab) => CARTEIRA_SEGMENT_TO_LABEL[tab];

/**
 * Parses a `/carteira/{id}/{aba?}/{subview?}` path. Returns the canonical
 * primary tab, optional sub-view, and a flag indicating whether the path is
 * structurally valid. Invalid combinations resolve to the default
 * "resumo" tab so callers always have a usable state.
 */
export const parseCarteiraResourcePath = (segments: ReadonlyArray<string>) => {
  const [idSegment, tabSegment, subviewSegment] = segments;

  if (!idSegment) {
    return {
      portfolioId: null,
      tab: 'resumo' as CarteiraPrimaryTab,
      dividendSubview: 'visao-geral' as CarteiraDividendSubview,
      patrimonySubview: 'visao-geral' as CarteiraPatrimonySubview,
      configSubview: 'geral' as CarteiraConfigSubview,
      isValid: false,
    };
  }

  let tab: CarteiraPrimaryTab = 'resumo';
  let dividendSubview: CarteiraDividendSubview = 'visao-geral';
  let patrimonySubview: CarteiraPatrimonySubview = 'visao-geral';
  let configSubview: CarteiraConfigSubview = 'geral';

  if (tabSegment && isCarteiraPrimaryTab(tabSegment)) {
    tab = tabSegment;
  } else if (tabSegment) {
    tab = 'resumo';
  }

  if (tab === 'dividendos' && subviewSegment) {
    dividendSubview = isCarteiraDividendSubview(subviewSegment) ? subviewSegment : 'visao-geral';
  }

  if (tab === 'patrimonio' && subviewSegment) {
    patrimonySubview = isCarteiraPatrimonySubview(subviewSegment) ? subviewSegment : 'visao-geral';
  }

  if (tab === 'configuracao' && subviewSegment) {
    configSubview = isCarteiraConfigSubview(subviewSegment) ? subviewSegment : 'geral';
  }

  return {
    portfolioId: idSegment,
    tab,
    dividendSubview,
    patrimonySubview,
    configSubview,
    isValid: true,
  };
};

export type CarteiraPath = {
  portfolioId: string;
  tab: CarteiraPrimaryTab;
  dividendSubview?: CarteiraDividendSubview;
  patrimonySubview?: CarteiraPatrimonySubview;
  configSubview?: CarteiraConfigSubview;
  categoria?: string;
};

const buildPath = ({
  portfolioId,
  tab,
  dividendSubview,
  patrimonySubview,
  configSubview,
  categoria,
}: CarteiraPath): string => {
  const base = `/carteira/${encodeURIComponent(portfolioId)}/${tab}`;
  let path: string;
  if (tab === 'dividendos' && dividendSubview && dividendSubview !== 'visao-geral') {
    path = `${base}/${dividendSubview}`;
  } else if (tab === 'patrimonio' && patrimonySubview && patrimonySubview !== 'visao-geral') {
    path = `${base}/${patrimonySubview}`;
  } else if (tab === 'configuracao' && configSubview && configSubview !== 'geral') {
    path = `${base}/${configSubview}`;
  } else {
    path = base;
  }
  return categoria && categoria !== 'all' ? `${path}?categoria=${encodeURIComponent(categoria)}` : path;
};

export const carteiraPath = {
  tab: (portfolioId: string, tab: CarteiraPrimaryTab, categoria?: string) => buildPath({ portfolioId, tab, categoria }),
  newPortfolio: () => CARTEIRA_NEW_PATH,
  index: () => CARTEIRA_INDEX_PATH,
  /**
   * Build a Carteira link preserving the active sub-view when crossing
   * tabs. The dividend/patrimony sub-views only attach when the new tab is
   * the matching one (otherwise they would leak to other pages).
   */
  withActiveSubviews: ({
    portfolioId,
    tab,
    dividendSubview,
    patrimonySubview,
    configSubview,
    categoria,
  }: CarteiraPath) =>
    buildPath({
      portfolioId,
      tab,
      dividendSubview,
      patrimonySubview,
      configSubview,
      categoria,
    }),
  /**
   * Preserves the canonical tab when switching portfolios or building
   * "Ver todos os ativos" style deep links. Defaults to "resumo" when
   * the caller has no opinion.
   */
  forTab: (portfolioId: string | null | undefined, tab: CarteiraPrimaryTab) =>
    portfolioId ? buildPath({ portfolioId, tab }) : `/carteira`,
};

/**
 * Legacy query-param parser. Kept for callers that still emit query
 * params (email preference toasts, import flow legacy redirects) so the
 * new path-based pages can be hydrated consistently. Returns the same
 * shape the path parser uses.
 */
export type CarteiraRouteSearchParams = {
  carteira?: string;
  aba?: string;
  nova?: string;
  error?: string;
  import?: string;
  imported?: string;
  fundType?: string;
  session?: string;
  draftName?: string;
  draftDescription?: string;
  dividendView?: string;
  includeAmortization?: string;
  forecastPeriod?: string;
  receivedGranularity?: string;
  receivedYear?: string;
  incomePeriod?: string;
  patrimonyView?: string;
  categoria?: string;
  reportStartDate?: string;
  reportEndDate?: string;
  category?: string;
  new?: string;
  tab?: string;
  portfolio?: string;
};

export const parseLegacyCarteiraSearchParams = (searchParams: CarteiraRouteSearchParams) => ({
  requestedPortfolioId: searchParams.carteira?.trim() ?? '',
  requestedTab: searchParams.aba,
  isCreatingNewPortfolio: searchParams.nova === '1',
  selectedCategory: searchParams.categoria,
});

/**
 * @deprecated Use {@link parseCarteiraResourcePath} for path-based routing.
 * Kept temporarily for the catalog/tests that still assert the legacy
 * contract; will be removed once the migration is complete.
 */
export const parseCarteiraRouteSelection = (searchParams: CarteiraRouteSearchParams) =>
  parseLegacyCarteiraSearchParams(searchParams);
